/* ──────────────────────────────────────────────
   Blooms Savings Calculator — Compact
   Bouquet-only, single-panel, monthly view
   ────────────────────────────────────────────── */

(function () {
  'use strict';

  const ROOT_ID = 'blooms-savings-compact';

  const COMPANY_LABELS = {
    horeca:        'Horeca',
    kantoor_klein: 'Kantoor klein',
    kantoor_groot: 'Kantoor groot',
    hotel:         'Hotel',
    hospitality:   'Hospitality',
    winkel:        'Retail / winkel',
  };

  const LIMITS = {
    xl: { min: 0, max: 15, step: 1 },
    m:  { min: 0, max: 15, step: 1 },
    s:  { min: 0, max: 15, step: 1 },
  };

  /* Verversingsfrequentie — swaps per maand (afgerond op hele swaps) */
  const FREQ = {
    weekly:   { label: 'Wekelijks',     swapsPerMonth: 4 },
    biweekly: { label: 'Om de 2 weken', swapsPerMonth: 2 },
    monthly:  { label: 'Maandelijks',   swapsPerMonth: 1 },
  };

  const WEEKS_PER_MONTH = 4.333;

  /* ── Utils ── */
  const fmtEUR = (n) =>
    '€' + Math.round(n).toLocaleString('nl-NL', { maximumFractionDigits: 0 });

  const fmtInt = (n) => Math.round(n).toLocaleString('nl-NL');

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  function animateNumber(el, to, format) {
    const from = Number(el.dataset.value || 0);
    if (from === to) { el.textContent = format(to); return; }
    el.dataset.value = to;
    const duration = 480;
    const start = performance.now();
    cancelAnimationFrame(el._raf || 0);
    function step(now) {
      const t = Math.min(1, (now - start) / duration);
      const val = from + (to - from) * easeOutCubic(t);
      el.textContent = format(val);
      if (t < 1) el._raf = requestAnimationFrame(step);
    }
    el._raf = requestAnimationFrame(step);
  }

  /* ── Calc — per maand ── */
  function calculate(state, pricing) {
    const bouquetCount = state.xl + state.m + state.s;
    const freq = FREQ[state.freq] || FREQ.weekly;
    const swaps = freq.swapsPerMonth;

    /* Fresh — per maand */
    const freshProduct =
      (state.xl * pricing.freshXL + state.m * pricing.freshM + state.s * pricing.freshS) * swaps;
    const freshMaintenance =
      (bouquetCount * pricing.minutesPerBouquet / 60) * pricing.hourlyRate * WEEKS_PER_MONTH;
    const freshHandling = bouquetCount * swaps * pricing.swapHandlingFee;
    const freshTotal = freshProduct + freshMaintenance + freshHandling;

    /* Blooms — per maand (abonnement) */
    const bloomsTotal =
      state.xl * pricing.bloomsXLPerMonth +
      state.m  * pricing.bloomsMPerMonth +
      state.s  * pricing.bloomsSPerMonth;

    /* Sustainability — verse boeketten vermeden per maand */
    const bouquetsAvoidedPerMonth = bouquetCount * swaps;
    const co2Avoided = bouquetsAvoidedPerMonth * pricing.co2PerBouquet;

    const savings = Math.max(0, freshTotal - bloomsTotal);
    const savedFlowers = Math.max(0, freshProduct - bloomsTotal);

    return {
      savings,
      savedFlowers,
      savedMaintenance: freshMaintenance,
      co2Avoided,
      hasBouquets: bouquetCount > 0,
    };
  }

  /* ── Render ── */
  function render(root, state, config, pricing, ctaCfg) {
    const result = calculate(state, pricing);

    root.innerHTML = `
      <div class="bscc-left">
        <div class="bscc-field">
          <label class="bscc-label">
            <span class="bscc-label-text">Type bedrijf</span>
          </label>
          <select class="bscc-select" data-field="company">
            ${Object.keys(config).map(key => `
              <option value="${key}"${state.company === key ? ' selected' : ''}>
                ${COMPANY_LABELS[key] || key}
              </option>
            `).join('')}
          </select>
        </div>

        ${sliderField('xl', 'XL boeketten', state.xl)}
        ${sliderField('m',  'M boeketten',  state.m)}
        ${sliderField('s',  'S boeketten',  state.s)}

        <div class="bscc-field">
          <div class="bscc-label">
            <span class="bscc-label-text">Verversingsfrequentie</span>
          </div>
          <div class="bscc-segment" role="radiogroup" aria-label="Verversingsfrequentie">
            ${Object.keys(FREQ).map(key => `
              <button type="button"
                      class="bscc-segment__btn${state.freq === key ? ' bscc-segment__btn--active' : ''}"
                      data-field="freq" data-value="${key}"
                      role="radio" aria-checked="${state.freq === key}">
                ${FREQ[key].label}
              </button>
            `).join('')}
          </div>
        </div>
      </div>

      <div class="bscc-right">
        <div>
          <h3 class="bscc-headline">
            <span class="bscc-headline__sign">€</span><span class="bscc-num-anim" data-num="savings">0</span>
            <span class="bscc-headline__unit">/ maand bespaard</span>
          </h3>
          <p class="bscc-headline-sub">Met Blooms boeketten abonnement t.o.v. verse bloemen.</p>
        </div>

        <hr class="bscc-divider">

        <div>
          <div class="bscc-line">
            <span class="bscc-line__amount"><span class="bscc-num-anim" data-num="savedFlowers">€0</span></span>
            <span class="bscc-line__label">minder uitgaven aan <strong>verse bloemen</strong></span>
          </div>
          <div class="bscc-line">
            <span class="bscc-line__amount"><span class="bscc-num-anim" data-num="savedMaintenance">€0</span></span>
            <span class="bscc-line__label">bespaard op <strong>onderhoud</strong> — Blooms vraagt geen tijd</span>
          </div>
          <div class="bscc-line">
            <span class="bscc-line__amount bscc-line__amount--accent"><span class="bscc-num-anim" data-num="co2Avoided">0</span> kg</span>
            <span class="bscc-line__label"><strong>CO₂ vermeden</strong> — geen import &amp; koeltransport</span>
          </div>
        </div>

        <div class="bscc-cta-wrap">
          ${ctaCfg.label ? `<p class="bscc-cta-header" style="flex:1 0 100%;margin:0 0 .25rem;">${ctaCfg.label}</p>` : ''}
          <a class="bscc-cta bscc-cta--primary" href="${ctaCfg.primaryUrl || '#'}">${ctaCfg.primaryLabel || 'Plan een demo'}</a>
          <a class="bscc-cta bscc-cta--secondary" href="${ctaCfg.secondaryUrl || '#'}">${ctaCfg.secondaryLabel || 'Bekijk abonnementen'}</a>
        </div>
      </div>
    `;

    updateNumbers(root, result, false);
    updateSliderProgress(root, state);
  }

  function sliderField(field, labelText, value) {
    const lim = LIMITS[field];
    return `
      <div class="bscc-field">
        <div class="bscc-label">
          <span class="bscc-label-text">${labelText}</span>
          <span class="bscc-pill" data-pill="${field}">${value}</span>
        </div>
        <input class="bscc-slider" type="range"
               data-field="${field}"
               min="${lim.min}" max="${lim.max}" step="${lim.step}"
               value="${value}">
        <div class="bscc-scale">
          <span>${lim.min}</span>
          <span>${lim.max}</span>
        </div>
      </div>
    `;
  }

  function updateSliderProgress(root, state) {
    root.querySelectorAll('.bscc-slider').forEach(sl => {
      const field = sl.dataset.field;
      const lim = LIMITS[field];
      const pct = ((state[field] - lim.min) / (lim.max - lim.min)) * 100;
      sl.style.setProperty('--bscc-progress', pct + '%');
    });
  }

  function updateNumbers(root, result, animate) {
    const euroMap = {
      savings:          result.savings,
      savedFlowers:     result.savedFlowers,
      savedMaintenance: result.savedMaintenance,
    };
    Object.keys(euroMap).forEach(key => {
      const el = root.querySelector('[data-num="' + key + '"]');
      if (!el) return;
      const val = euroMap[key];
      if (key === 'savings') {
        if (animate) animateNumber(el, val, (n) => fmtInt(n));
        else el.textContent = fmtInt(val);
      } else {
        if (animate) animateNumber(el, val, fmtEUR);
        else el.textContent = fmtEUR(val);
      }
    });

    const plainMap = { co2Avoided: result.co2Avoided };
    Object.keys(plainMap).forEach(key => {
      const el = root.querySelector('[data-num="' + key + '"]');
      if (!el) return;
      if (animate) animateNumber(el, plainMap[key], fmtInt);
      else el.textContent = fmtInt(plainMap[key]);
    });
  }

  /* ── Init ── */
  function init() {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;

    const config  = JSON.parse(root.dataset.config);
    const pricing = JSON.parse(root.dataset.pricing);
    const ctaCfg = {
      label:          root.dataset.ctaLabel,
      primaryLabel:   root.dataset.ctaPrimaryLabel,
      primaryUrl:     root.dataset.ctaPrimaryUrl,
      secondaryLabel: root.dataset.ctaSecondaryLabel,
      secondaryUrl:   root.dataset.ctaSecondaryUrl,
    };

    const initial = 'horeca';
    const state = {
      company: initial,
      xl:   config[initial].xl,
      m:    config[initial].m,
      s:    config[initial].s || 0,
      freq: config[initial].freq || 'weekly',
    };

    render(root, state, config, pricing, ctaCfg);

    /* Slider + select input */
    root.addEventListener('input', (e) => {
      const field = e.target.dataset.field;
      if (!field) return;

      if (field === 'company') {
        const key = e.target.value;
        state.company = key;
        state.xl   = config[key].xl;
        state.m    = config[key].m;
        state.s    = config[key].s || 0;
        state.freq = config[key].freq || 'weekly';
        render(root, state, config, pricing, ctaCfg);
        updateNumbers(root, calculate(state, pricing), true);
        return;
      }

      if (!(field in LIMITS)) return;
      let val = parseInt(e.target.value, 10);
      if (Number.isNaN(val)) val = LIMITS[field].min;
      val = Math.max(LIMITS[field].min, Math.min(LIMITS[field].max, val));
      state[field] = val;

      const pill = root.querySelector('[data-pill="' + field + '"]');
      if (pill) pill.textContent = val;

      updateNumbers(root, calculate(state, pricing), true);
      updateSliderProgress(root, state);
    });

    /* Frequency segmented control */
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-field="freq"]');
      if (!btn) return;
      const val = btn.dataset.value;
      if (!FREQ[val] || state.freq === val) return;
      state.freq = val;
      root.querySelectorAll('[data-field="freq"]').forEach(b => {
        const isActive = b.dataset.value === val;
        b.classList.toggle('bscc-segment__btn--active', isActive);
        b.setAttribute('aria-checked', String(isActive));
      });
      updateNumbers(root, calculate(state, pricing), true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
